import { inject, Injectable } from '@angular/core';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { Page } from '../../../shared/model/local/pagination/page';
import { MessageActions } from './message.actions';
import { MessageApiService } from '../service/message.api.service';
import { Message } from '../model/message';
import { MessagePageCriteria } from '../model/message-page-criteria';

export interface MessageStateModel {
  page: Page<Message> | null;
  pageCriteria: MessagePageCriteria | null;
}

@State<MessageStateModel>({
  name: 'messages',
  defaults: {
    page: null,
    pageCriteria: null,
  },
})
@Injectable()
export class MessageState {
  private readonly api = inject(MessageApiService);

  static page() {
    return createSelector(
      [MessageState],
      (state: MessageStateModel) => state.page
    );
  }

  static pageCriteria() {
    return createSelector(
      [MessageState],
      (state: MessageStateModel) => state.pageCriteria
    );
  }

  @Action(MessageActions.GetPage, { cancelUncompleted: true })
  async getPage(
    ctx: StateContext<MessageStateModel>,
    action: MessageActions.GetPage
  ) {
    const currentCriteria = ctx.getState().pageCriteria;
    // Clear page data if status changed to avoid showing stale data
    if (currentCriteria?.status !== action.criteria.status) {
      ctx.patchState({ page: null });
    }
    const page = await firstValueFrom(this.api.getPage(action.criteria));
    ctx.patchState({
      page,
      pageCriteria: action.criteria,
    });
  }

  @Action(MessageActions.RefreshPage)
  async refreshPage(ctx: StateContext<MessageStateModel>) {
    const currentCriteria = ctx.getState().pageCriteria;
    if (currentCriteria) {
      const page = await firstValueFrom(this.api.getPage(currentCriteria));
      ctx.patchState({ page });
    }
  }

  @Action(MessageActions.ClearState)
  clearState(ctx: StateContext<MessageStateModel>) {
    ctx.setState({
      page: null,
      pageCriteria: null,
    });
  }
}
